import * as Sentry from '@sentry/react';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { broadcastLogout, clearAuthState } from '@/helpers/authCleanup';

import { BASE_URL } from '../config';

/**
 * Token refresh singleton state.
 * Inspired by Supabase's deferred promise pattern - uses the presence
 * of the promise itself to indicate refresh is in progress.
 */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Flag to track if logout is already in progress.
 * Prevents duplicate handleAuthFailure calls from concurrent requests.
 */
let logoutInProgress = false;

/**
 * Flag to mark requests that have already attempted token refresh.
 * Prevents infinite retry loops.
 */
const RETRY_FLAG = '_retry' as const;

/**
 * Extended request config type that includes retry tracking.
 */
type RetryableRequestConfig = InternalAxiosRequestConfig & {
  [RETRY_FLAG]?: boolean;
};

const axiosApiService = axios.create();

/**
 * Centralized authentication failure handler.
 * Clears all auth state, broadcasts logout to other tabs, and redirects to login.
 * Idempotent - safe to call multiple times (subsequent calls are no-ops).
 */
function handleAuthFailure(): void {
  // Prevent duplicate logout calls from concurrent requests
  if (logoutInProgress) {
    return;
  }

  logoutInProgress = true;
  console.error('Authentication failed - logging out');
  clearAuthState();
  broadcastLogout();
  window.location.pathname = '/login';
}

/**
 * Refreshes the access token using the refresh token.
 *
 * Uses singleton promise pattern (inspired by Supabase auth-js) to prevent
 * race conditions when multiple requests fail with 401 simultaneously.
 *
 * Note: Does NOT save new refresh_token from response to preserve fixed
 * expiry behavior for Remember Me feature.
 *
 * @param capturedRefreshToken - The refresh token captured from localStorage at call time
 * @returns Promise resolving to new access token, or null if refresh fails
 */
async function refreshAccessToken(
  capturedRefreshToken: string,
): Promise<string | null> {
  // If refresh already in progress, return existing promise (race condition protection)
  if (refreshPromise) {
    return refreshPromise;
  }

  // Double-check lock: Another request might have started refresh while we were waiting
  if (refreshPromise) {
    return refreshPromise;
  }

  // IMPORTANT: Use base axios instance (not axiosApiService) to prevent
  // interceptor recursion if refresh endpoint returns 401
  refreshPromise = axios
    .put<{ data: { access_token: string; refresh_token: string } }>(
      `${BASE_URL}/api/session`,
      { refresh_token: capturedRefreshToken },
      { timeout: 10000 },
    )
    .then(res => {
      if (res.status === 200 && res.data?.data?.access_token) {
        const newAccessToken = res.data.data.access_token;
        localStorage.setItem('access_token', newAccessToken);
        // Note: NOT saving refresh_token - preserves original expiry for Remember Me
        return newAccessToken;
      }
      return null;
    })
    .catch(error => {
      // Track refresh failures in Sentry for monitoring
      Sentry.captureException(error, {
        tags: { feature: 'token_refresh' },
        extra: {
          hasRefreshToken: true,
          responseStatus: error.response?.status,
          responseData: error.response?.data,
        },
      });
      return null;
    })
    .finally(() => {
      // Always clean up promise reference, even on error
      refreshPromise = null;
    });

  return refreshPromise;
}

// request interceptor to add the auth token header to requests
axiosApiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && !config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Response interceptor to handle 401 errors with automatic token refresh.
 *
 * Flow:
 * 1. Network errors → reject immediately (per requirements)
 * 2. 401 with retry flag → prevent infinite loop, logout
 * 3. 401 with refresh token → attempt refresh, retry with new token
 * 4. 401 without refresh token → logout
 * 5. Other errors → pass through
 */
axiosApiService.interceptors.response.use(
  response => response,
  async (error: {
    config: RetryableRequestConfig;
    response?: AxiosResponse;
  }) => {
    // Network errors (no response object) - immediate failure
    if (!error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // Only handle 401 Unauthorized errors
    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    // Infinite loop protection: Check if we've already retried this request
    if (originalRequest[RETRY_FLAG]) {
      console.error(
        'Token refresh already attempted for this request - logging out',
      );
      handleAuthFailure();
      return Promise.reject(error);
    }

    // Capture BOTH tokens atomically to prevent TOCTOU race conditions
    // Must capture together to ensure consistent state (another tab could clear them)
    const refreshToken = localStorage.getItem('refresh_token');
    const accessToken = localStorage.getItem('access_token');

    // Has refresh token - attempt token refresh
    if (refreshToken) {
      // Mark request as retried BEFORE attempting refresh (prevent loops)
      originalRequest[RETRY_FLAG] = true;

      // Attempt refresh (deduplicates concurrent refresh requests)
      // Pass captured token to prevent race condition if localStorage is cleared
      const newToken = await refreshAccessToken(refreshToken);

      if (newToken) {
        // Success - update Authorization header and retry original request
        // Use explicit header (bypasses request interceptor for deterministic behavior)
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      }

      // Refresh failed - logout
      handleAuthFailure();
      return Promise.reject(error);
    }

    // No refresh token available
    // Only logout if we have an access token (session existed but can't refresh)
    // If no tokens at all, we're already logged out - just reject
    if (accessToken) {
      handleAuthFailure();
    }

    return Promise.reject(error);
  },
);

export default axiosApiService;
